import { Group, Stack, Text, Title } from "@mantine/core"
import { InfoBox, Card } from "@tfg/ui"
import _ from "lodash"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { ActivityBadge } from "~/components/ActivityBadge.tsx"
import { CountryCell } from "~/components/cells/CountryCell.tsx"

type Props = {
  product: LibraryResponses.ProductDetails
}

export const SuppliersList = ({ product }: Props) => {
  const { t } = useTranslation("products")

  const { publicSuppliers } = useMemo(() => {
    const productItem = product.item

    const allOperations = productItem.operations
      .concat(
        product.components.flatMap((component) =>
          component.item.operations.concat(
            component.materials.flatMap((material) => material.item.operations)
          )
        )
      )
      .filter((op) => op.productionSite.status !== "not-entered-yet")

    const operationsByAddress = _.groupBy(
      allOperations,
      (operation) => operation.productionSite.value?.address
    )

    const publicSuppliers = Object.values(operationsByAddress).map(
      (operations) => {
        const firstOperation = operations[0]

        const activities = _.uniq(operations.map((op) => op.activityName))

        return {
          companyName: firstOperation.productionSite.value?.companyName,
          address: firstOperation.productionSite.value?.address,
          country: firstOperation.country,
          activities,
        }
      }
    )

    return { publicSuppliers }
  }, [product])

  return (
    <Card w="fit-content">
      <Stack w={600}>
        {publicSuppliers.length === 0 && (
          <InfoBox color="gray" message={t("details.suppliers.noSuppliers")} />
        )}
        {publicSuppliers.length > 0 && (
          <Stack>
            <Title order={4}>
              {t("details.suppliers.publicTitle", {
                count: publicSuppliers.length,
              })}
            </Title>
            {publicSuppliers.map((supplier) => {
              return (
                <Stack
                  py="md"
                  gap="xs"
                  key={supplier.address}
                  style={{ borderBottom: "1px solid lightgray" }}
                >
                  <Group justify="space-between">
                    <Text fw={700}>{supplier.companyName}</Text>
                    <CountryCell
                      countryIso3166={supplier.country.value}
                      size="sm"
                      fw={700}
                    />
                  </Group>

                  <Text size="sm">{supplier.address}</Text>
                  <Group gap="xs">
                    {supplier.activities.map((activity) => (
                      <ActivityBadge key={activity} activityName={activity} />
                    ))}
                  </Group>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
